import { Section } from "@/components/layout/Section";
import { ContactForm } from "@/components/forms/ContactForm";
import { LogoMarquee } from "@/components/LogoMarquee";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import { useEffect, useRef } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import {
  trackCTAClick,
  trackCallBookingStarted,
  trackFaqOpened,
  trackScrollTerms,
  trackPhoneCall,
} from "@/lib/analytics";
import { Phone, MessageSquare } from "lucide-react";
import { BOOK_A_CALL_URL } from "@/lib/cta";
import {
  generateFAQSchema,
  generateBreadcrumbSchema,
  organizationSchema,
  websiteSchema,
} from "@/lib/structured-data";
import { motion } from "framer-motion";
import { fadeUp, staggerContainer, smoothSpring, viscousEase, viewportOnce } from "@/lib/motion-variants";
import { Link } from "react-router-dom";
import { useArticles } from "@/hooks/useArticles";
import { PulseTeaser } from "@/components/PulseTeaser";
import whyNowCollage from "@/assets/homepage/why-now-collage.jpg.asset.json";
import pillarStrategyCollage from "@/assets/homepage/pillar-strategy-collage.jpg.asset.json";
import pillarExecutionCollage from "@/assets/homepage/pillar-execution-collage.jpg.asset.json";
import pillarCommunityCollage from "@/assets/homepage/pillar-community-collage.jpg.asset.json";



/* ─── FAQ (spec Section 8) ─── */
const homepageFAQs = [
  {
    question: "What does The Zoom Agency do?",
    answer:
      "We are a senior creative and performance marketing agency. We run brand strategy, creative, content, paid media, and community and social management for B2B and B2C companies, working as an embedded part of the client's business.",
  },
  {
    question: "Who do you work with?",
    answer:
      "B2B and B2C companies at every stage, from early-stage businesses without a marketing hire to enterprises with large internal teams. We are based in London and work with companies across the UK and United States.",
  },
  {
    question: "How quickly will we see results?",
    answer:
      "First measurable results typically arrive within 90 days. The return compounds from there, and most clients stay for years because the work keeps earning it.",
  },
  {
    question: "How do you price your work?",
    answer:
      "On deliverables and outcomes. We never bill by the hour, so gains in our efficiency benefit you rather than inflating an invoice.",
  },
  {
    question: "Do you replace our in-house team?",
    answer:
      "We work as your marketing function when you have none, and alongside your team when you do. Several of our longest engagements run inside internal marketing teams of thirty people or more.",
  },
  {
    question: "How do you use superintelligence?",
    answer:
      "Across research, production and reporting, with disciplined inputs and senior review on every deliverable. Our founder teaches Generative AI for Marketing at IE Business School, and the same methods run through our client work.",
  },
];

const SITE_URL = "https://www.thezoomagency.com";

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: "The Zoom Agency",
  url: SITE_URL,
  description:
    "Senior creative and performance marketing agency for B2B and B2C companies in the UK and United States, based in London. Founded 2013.",
  foundingDate: "2013",
  founder: {
    "@type": "Person",
    name: "Ben Walker",
    jobTitle: "Founder",
    sameAs: ["https://www.linkedin.com/in/benwalker-marketing/"],
  },
  address: { "@type": "PostalAddress", addressLocality: "London", addressCountry: "GB" },
  telephone: "+44 203 488 3090",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    telephone: "+44 203 488 3090",
    availableLanguage: ["English"],
  },
  areaServed: [
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "United States" },
  ],
  sameAs: ["https://www.linkedin.com/company/the-zoom-agency"],
};

const homeStructuredData = [
  organizationSchema,
  websiteSchema,
  localBusinessSchema,
  generateFAQSchema(homepageFAQs, "/"),
  generateBreadcrumbSchema([{ name: "Home", url: "/" }]),
];

export default function Index() {
  const termsRef = useRef<HTMLElement | null>(null);
  const termsFired = useRef(false);
  const { articles: latestArticles, loading: latestArticlesLoading } = useArticles(3);

  // Warm-neutral theme is now applied site-wide in main.tsx; no page-scoped toggle needed.


  // scroll_terms — fires once when Section 4 enters viewport
  useEffect(() => {
    if (!termsRef.current) return;
    const el = termsRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !termsFired.current) {
            termsFired.current = true;
            trackScrollTerms();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const scrollToContact = (location: "hero" | "nav" | "mid_page" | "final") => {
    trackCallBookingStarted(location);
    trackCTAClick("Book a call", location);
    document.getElementById("conversation")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className="pt-20" id="homepage">
      <SEO
        title="The Zoom Agency | Creative & Performance Marketing"
        description="Senior creative and performance marketing team for UK and US brands. Embedded in your business since 2013. Measurable results within 90 days."
        canonical="/"
        structuredData={homeStructuredData}
      />

      {/* ─── 1. HERO: inset Ember card ─── */}
      <section className="-mt-20 pt-[88px] px-4 pb-4">
        <div className="grain bg-gradient-ember relative overflow-hidden rounded-4xl min-h-[max(640px,calc(100svh-104px))] flex items-end">
          <div className="w-full p-6 sm:p-10 lg:p-16 text-background">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.p variants={fadeUp} transition={smoothSpring} className="eyebrow-chip mb-8">
                Creative and performance marketing since 2013
              </motion.p>
              <motion.h1
                variants={fadeUp}
                transition={smoothSpring}
                className="hero-headline text-display max-w-[14ch] mb-8"
              >
                The marketing agency your in-house team would choose.
              </motion.h1>
              <motion.div variants={fadeUp} transition={smoothSpring} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                <p className="hero-description text-body-lg max-w-[440px]">
                  We take charge of marketing for B2B and B2C companies in the UK and United States, run it as if the company were ours,
                  and prove its worth in your growth within 90 days.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild variant="onDark">
                    <a
                      href={BOOK_A_CALL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => { trackCallBookingStarted("hero"); trackCTAClick("Book a call", "hero"); }}
                    >
                      Book a call
                    </a>
                  </Button>
                  <Button
                    onClick={() => { trackCTAClick("Contact us", "hero"); document.getElementById("conversation")?.scrollIntoView({ behavior: "smooth" }); }}
                    variant="onDarkSecondary"
                  >
                    Contact us
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>



      {/* ─── 2. CLIENT STRIP ─── */}
      <Section size="md" className="border-t border-border/60">
        <p className="text-center text-[11px] uppercase tracking-[0.28em] font-medium text-muted-foreground mb-6">
          Trusted by
        </p>
        <LogoMarquee />
        <p className="text-center text-sm text-muted-foreground max-w-2xl mx-auto mt-8">
          Long-term partners across luxury, consumer and industrial brands. Our client relationships are measured in years.
        </p>
      </Section>

      {/* ─── 3. WHY NOW ─── */}
      <Section id="why-now" className="border-t border-border/60 bg-surface-recessed">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
        >
          <div>
            <motion.h2
              variants={fadeUp}
              transition={viscousEase}
              className="font-serif text-display-sm md:text-display-md text-foreground mb-6 text-pretty"
            >
              Production is cheap now. Judgement is expensive.
            </motion.h2>
            <motion.p variants={fadeUp} transition={viscousEase} className="text-body-lg text-muted-foreground leading-relaxed">
              Anyone can generate content at volume in 2026. Most companies do, and most of it is ignored. The value has moved to
              strategy, creative judgement and consistent execution. That is where we work: senior specialists, creative and
              performance under one team, embedded alongside your people or acting as them.
            </motion.p>
          </div>
          <motion.div variants={fadeUp} transition={{ ...viscousEase, delay: 0.1 }} className="rounded-2xl overflow-hidden aspect-[4/3] bg-muted/40">
            <img
              src={whyNowCollage.url}
              alt="Editorial collage of layered paper cut-outs with one sheet pulled forward, symbolising creative judgement and curation."
              width={1024}
              height={768}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </motion.div>
      </Section>

      {/* ─── 4. WHAT WE DO ─── */}
      <Section id="what-we-do" className="border-t border-border/60">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce}>
          <motion.h2
            variants={fadeUp}
            transition={viscousEase}
            className="font-serif text-display-sm md:text-display-md text-foreground mb-12 md:mb-16 max-w-3xl text-pretty"
          >
            Three things, done properly.
          </motion.h2>

          <motion.h3
            variants={fadeUp}
            transition={viscousEase}
            className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-8"
          >
            How it comes together
          </motion.h3>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {[
              {
                num: "01",
                title: "Strategy, creative and performance, one team.",
                image: pillarStrategyCollage.url,
                alt: "Editorial collage of a thoughtful professional surrounded by layered paper plans and a blue connecting line, representing joined-up strategy, creative and performance.",
                body:
                  "Brand strategy, creative, content and paid media run by the same senior people, so the work and the numbers answer to each other. No handovers between a creative shop and a media shop, and no gap for results to fall into. We run this for B2B companies selling complex products and B2C brands fighting for attention, and the discipline is the same: creative that performs, performance with ideas behind it.",
              },
              {
                num: "02",
                title: "Execution and management.",
                image: pillarExecutionCollage.url,
                alt: "Editorial collage of hands arranging layered paper cut-outs into a clear sequence with a blue progress marker, representing focused execution and daily ownership.",
                body:
                  "Strategy is worth what gets executed. We manage the calendar, run the channels, ship the campaigns and report on what they returned, week after week. Embedded in your tools, your meetings and your targets, we take ownership of the day-to-day so marketing keeps moving whether or not you are watching it.",
              },
              {
                num: "03",
                title: "Community and social.",
                image: pillarCommunityCollage.url,
                alt: "Editorial collage of overlapping diverse portraits connected by a continuous blue arc, representing communities that compound over time.",
                body:
                  "Audiences that come back are worth more than audiences you rent. We build and manage communities around brands: daily social management, ambassador programmes, influencer relationships and social listening that feeds back into strategy and product. Real presence, real conversations, managed by people senior enough to speak for your brand.",
              },
            ].map((pillar, i) => (
              <motion.div
                key={pillar.num}
                variants={fadeUp}
                transition={{ ...smoothSpring, delay: i * 0.08 }}
                className="border-t border-border pt-6"
              >
                <div className="rounded-2xl overflow-hidden mb-6 aspect-[4/3] bg-muted/40">
                  <img
                    src={pillar.image}
                    alt={pillar.alt}
                    width={1024}
                    height={768}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="font-mono text-xs text-primary mb-4">{pillar.num}</div>
                <h3 className="font-serif text-heading-lg text-foreground mb-4 leading-snug text-pretty">{pillar.title}</h3>
                <p className="text-body-md text-muted-foreground leading-relaxed">{pillar.body}</p>
              </motion.div>
            ))}
          </div>

        </motion.div>
      </Section>

      {/* ─── 5. WHO WE WORK WITH ─── */}
      <Section id="who-we-work-with" className="border-t border-border/60">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce}>
          <motion.h2
            variants={fadeUp}
            transition={viscousEase}
            className="font-serif text-display-sm md:text-display-md text-foreground mb-6 max-w-3xl text-pretty"
          >
            From your first marketer to your full marketing function.
          </motion.h2>
          <motion.p variants={fadeUp} transition={viscousEase} className="text-body-lg text-muted-foreground max-w-prose mb-12 md:mb-16">
            We work with B2B and B2C companies at every stage.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
            {[
              {
                label: "If you are early stage",
                body:
                  "You need marketing that works before you can afford to hire for it. We become your marketing function: strategy, execution and reporting, run by senior people, sized to your stage. When you are ready to hire in-house, we help you build the team and hand over properly.",
              },
              {
                label: "If you have a marketing team",
                body:
                  "In-house teams are strongest with the right partners around them. We slot in for strategy, creative, performance or community, take real ownership of targets, and make your team better while we are there.",
              },
            ].map((group, i) => (
              <motion.div
                key={group.label}
                variants={fadeUp}
                transition={{ ...smoothSpring, delay: i * 0.1 }}
                className="border-t border-border pt-6"
              >
                <h3 className="font-serif text-heading-lg text-foreground mb-4">{group.label}</h3>
                <p className="text-body-md text-muted-foreground leading-relaxed">{group.body}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* ─── 4b. LATEST INSIGHTS ─── */}
      <Section size="md" id="insights" className="border-t border-border/60 bg-surface-recessed">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <motion.div
            variants={fadeUp}
            transition={viscousEase}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
          >
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Insights</p>
              <h2 className="font-serif text-display-sm md:text-display-md text-foreground text-pretty">
                Fresh thinking on marketing, superintelligence and growth.
              </h2>
            </div>
            <Link
              to="/insights"
              className="text-sm font-medium text-primary hover:underline underline-offset-4 self-start md:self-end"
            >
              View all insights →
            </Link>
          </motion.div>

          {latestArticlesLoading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg bg-background border border-border/20 overflow-hidden h-full animate-pulse"
                >
                  <div className="aspect-[16/9] bg-muted" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-16 bg-muted rounded" />
                    <div className="h-5 w-3/4 bg-muted rounded" />
                    <div className="h-4 w-full bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <motion.div key={article.slug} variants={fadeUp} transition={viscousEase}>
                  <Link
                    to={`/insights/${article.slug}`}
                    className="group block bg-background rounded-lg overflow-hidden border border-border/20 hover:border-primary/40 transition-colors h-full"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <span className="text-xs font-medium text-primary uppercase tracking-wider">
                        {article.category}
                      </span>
                      <h3 className="font-serif text-heading-md text-foreground mt-2 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-body-md text-muted-foreground leading-relaxed line-clamp-3 mb-3">
                        {article.excerpt}
                      </p>
                      <span className="text-sm text-muted-foreground">
                        {article.date} · {article.readTime}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </Section>

      {/* ─── 4c. PULSE TEASER ─── */}
      <PulseTeaser />



      {/* ─── 6. HOW WE WORK (terms) ─── */}
      <section ref={termsRef} id="terms" className="border-t border-border/60 bg-surface-recessed relative">
        <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12 py-20 md:py-24">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce}>
            <motion.h2
              variants={fadeUp}
              transition={viscousEase}
              className="font-serif text-display-sm md:text-display-md text-foreground mb-12 md:mb-16 max-w-3xl text-pretty"
            >
              The terms we work on.
            </motion.h2>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {[
                {
                  title: "Contracts sized to the work.",
                  body:
                    "Performance marketing engagements start from three months, social and community from six. Most of our relationships run for years, because the results earn the renewal.",
                },
                {
                  title: "Senior only.",
                  body: "The people who plan your work do your work. We do not staff juniors on client accounts.",
                },
                {
                  title: "Priced on deliverables and outcomes, never on hours.",
                  body: "You buy results, and our efficiency is our problem.",
                },
                {
                  title: "Built to fit your team.",
                  body:
                    "We work as your marketing function or alongside it, in your tools and your meetings, accountable to your targets.",
                },
              ].map((term, i) => (
                <motion.li
                  key={term.title}
                  variants={fadeUp}
                  transition={{ ...smoothSpring, delay: i * 0.08 }}
                  className="flex gap-4"
                >
                  <span
                    aria-hidden
                    className="mt-1 h-6 w-6 flex-shrink-0 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center"
                  >
                    <svg viewBox="0 0 12 12" className="h-3 w-3 text-primary" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 6.5L5 9.5L10 3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-serif text-heading-md text-foreground mb-2 text-pretty">{term.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{term.body}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ─── 7. HOW WE USE superintelligence ─── */}
      <Section id="ai" className="border-t border-border/60">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="max-w-3xl"
        >
          <motion.h2
            variants={fadeUp}
            transition={viscousEase}
            className="font-serif text-display-sm md:text-display-md text-foreground mb-8 text-pretty"
          >
            We know what to feed the system.
          </motion.h2>
          <motion.div variants={fadeUp} transition={viscousEase} className="space-y-6 text-body-lg text-muted-foreground leading-relaxed">
            <p>
              superintelligence runs through everything we do, from research to production to reporting. It is also why most marketing now looks the
              same: generic inputs produce generic output, and audiences can tell.
            </p>
            <p>
              The difference is what goes in and who signs it off. We feed the system properly: brand guidelines, audience data,
              worked examples and a tone of voice defined tightly enough that the output sounds like you. We prompt as a craft,
              refined daily across every client we run. And every deliverable passes a senior specialist before it reaches you.
            </p>
            <p>
              The result is the speed of superintelligence at the standard of a senior team. Work ships faster, tests more variations and reads like
              it came from inside your company, because in every way that matters, it did.
            </p>
          </motion.div>
        </motion.div>
      </Section>

      {/* ─── 8. CONTACT (form) ─── */}
      <Section
        id="conversation"
        size="md"
        className="border-t border-border/60 bg-primary text-primary-foreground"
      >
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <motion.div variants={fadeUp} transition={viscousEase} className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="font-serif text-display-sm md:text-display-md text-primary-foreground mb-6 text-pretty">
              Start a conversation.
            </h2>
            <p className="text-body-lg text-primary-foreground/85 leading-relaxed">
              Tell us what you are trying to solve. We will come back with a clear view of how we can help, whether that is a
              senior-led strategy, embedded execution, or a specific campaign.
            </p>
            <p className="text-sm text-primary-foreground/70 mt-6">No pitch deck, no obligation.</p>
          </motion.div>
          <ContactForm />
        </motion.div>
      </Section>

      {/* ─── 9. FOUNDER ─── */}
      <Section id="founder" className="relative isolate overflow-hidden border-t border-border/60 bg-surface-recessed">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="max-w-3xl"
        >
          <motion.h2
            variants={fadeUp}
            transition={viscousEase}
            className="font-serif text-display-sm md:text-display-md text-foreground mb-8 text-pretty"
          >
            Led from the front since 2013.
          </motion.h2>
          <motion.p variants={fadeUp} transition={viscousEase} className="text-body-lg text-muted-foreground leading-relaxed">
            Ben Walker founded The Zoom Agency in 2013 and still leads performance across the client base. He teaches Generative superintelligence
            for Marketing at IE Business School and has built the agency around one belief: senior thinking, applied consistently,
            beats volume every time. Based in London, working across the UK and United States.
          </motion.p>
        </motion.div>
      </Section>


      {/* ─── 10. FAQ ─── */}
      <Section size="sm" id="faq" className="border-t border-border/60">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10"
        >
          <motion.div variants={fadeUp} transition={viscousEase} className="lg:col-span-4">
            <h2 className="font-serif text-display-sm md:text-display-md text-foreground mb-4 text-pretty">
              Questions we get asked.
            </h2>
          </motion.div>
          <motion.div variants={fadeUp} transition={viscousEase} className="lg:col-span-8">
            <Accordion
              type="single"
              collapsible
              className="w-full"
              onValueChange={(v) => {
                if (!v) return;
                const idx = parseInt(v.replace("item-", ""), 10);
                const q = homepageFAQs[idx]?.question;
                if (q) trackFaqOpened(q);
              }}
            >
              {homepageFAQs.map((faq, i) => (
                <AccordionItem key={faq.question} value={`item-${i}`}>
                  <AccordionTrigger className="font-serif text-heading-md md:text-heading-lg text-foreground text-left hover:no-underline hover:text-primary py-5">
                    <h3 className="font-serif text-heading-md md:text-heading-lg m-0">{faq.question}</h3>
                  </AccordionTrigger>
                  <AccordionContent className="text-body-md text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </motion.div>
      </Section>

      {/* ─── 11. FINAL CTA ─── */}
      <Section id="final-cta" size="md" className="border-t border-border/60 bg-surface-recessed">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="max-w-3xl mx-auto text-center"
        >
          <motion.h2
            variants={fadeUp}
            transition={viscousEase}
            className="font-serif text-display-sm md:text-display-md text-foreground mb-6 text-pretty"
          >
            If your marketing has to prove itself this year, talk to us.
          </motion.h2>
          <motion.p variants={fadeUp} transition={viscousEase} className="text-body-lg text-muted-foreground leading-relaxed mb-10">
            One call. Bring your hardest problem. You will leave with a sharper view of it whether we work together or not.
          </motion.p>
          <motion.div variants={fadeUp} transition={viscousEase} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild variant="hero" size="lg">
              <a
                href={BOOK_A_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { trackCallBookingStarted("final"); trackCTAClick("Book a call", "final"); }}
              >
                Book a call
              </a>
            </Button>
            <Button
              onClick={() => { trackCTAClick("Contact us", "final"); document.getElementById("conversation")?.scrollIntoView({ behavior: "smooth" }); }}
              variant="outline"
              size="lg"
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              Contact us
            </Button>
            <a
              href="tel:+442034883090"
              onClick={() => trackPhoneCall("final_cta")}
              className="inline-flex items-center gap-2 text-body-md font-medium text-foreground hover:text-primary transition-colors"
              aria-label="Call us on +44 (0) 203 488 3090"
            >
              <Phone size={18} />
              +44 (0) 203 488 3090
            </a>
          </motion.div>
        </motion.div>
      </Section>

      <StickyMobileCTA />
    </main>
  );
}
